// src/components/tutorials/JS_String_Complete.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button (opens code in /editorJS)
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_String_Methods() {
  // refs for each section so links can scroll to them
  const refs = {
    length: useRef(null),
    charAt: useRef(null),
    charCodeAt: useRef(null),
    codePointAt: useRef(null),
    concat: useRef(null),
    at: useRef(null),
    bracket: useRef(null),
    slice: useRef(null),
    substring: useRef(null),
    substr: useRef(null),
    toUpperCase: useRef(null),
    toLowerCase: useRef(null),
    isWellFormed: useRef(null),
    toWellFormed: useRef(null),
    trim: useRef(null),
    trimStart: useRef(null),
    trimEnd: useRef(null),
    padStart: useRef(null),
    padEnd: useRef(null),
    repeat: useRef(null),
    replace: useRef(null),
    replaceAll: useRef(null),
    split: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // left and right columns for header (labels match target keys)
  const leftLinks = [
    { key: "length", label: "String length" },
    { key: "charAt", label: "String charAt()" },
    { key: "charCodeAt", label: "String charCodeAt()" },
    { key: "codePointAt", label: "String codePointAt()" },
    { key: "concat", label: "String concat()" },
    { key: "at", label: "String at()" },
    { key: "bracket", label: "String [ ]" },
    { key: "slice", label: "String slice()" },
    { key: "substring", label: "String substring()" },
    { key: "substr", label: "String substr()" },
    { key: "toUpperCase", label: "String toUpperCase()" },
    { key: "toLowerCase", label: "String toLowerCase()" },
  ];

  const rightLinks = [
    { key: "isWellFormed", label: "String isWellFormed()" },
    { key: "toWellFormed", label: "String toWellFormed()" },
    { key: "trim", label: "String trim()" },
    { key: "trimStart", label: "String trimStart()" },
    { key: "trimEnd", label: "String trimEnd()" },
    { key: "padStart", label: "String padStart()" },
    { key: "padEnd", label: "String padEnd()" },
    { key: "repeat", label: "String repeat()" },
    { key: "replace", label: "String replace()" },
    { key: "replaceAll", label: "String replaceAll()" },
    { key: "split", label: "String split()" },
  ];

  return (
    <div className="tutorial-container">
      {/* Header / Key points (looks like your screenshot) */}
      {/* Title */}
      <h1 className="tutorial-title">String Methods</h1>

      {/* Introduction */}
      <p className="tutorial-intro">
        JavaScript strings are <strong>primitive and immutable</strong>: All
        string methods produce a new string without altering the original.
      </p>

      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
        }}
      >
        <h1 style={{ color: "#92400e", fontSize: "2.4rem", margin: 0 }}>
          Basic String Methods
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          JavaScript strings are primitive and immutable: All string methods
          produce a new string without altering the original string.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            marginTop: "1.25rem",
          }}
        >
          <div>
            {leftLinks.map((l) => (
              <p key={l.key} style={{ margin: "0.25rem 0" }}>
                <button
                  onClick={() => scrollTo(l.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    textDecoration: "underline",
                    color: "#000000ff",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                  }}
                >
                  {l.label}
                </button>
              </p>
            ))}
          </div>
          <div>
            {rightLinks.map((l) => (
              <p key={l.key} style={{ margin: "0.25rem 0" }}>
                <button
                  onClick={() => scrollTo(l.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    textDecoration: "underline",
                    color: "#000000ff",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                  }}
                >
                  {l.label}
                </button>
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* Sections: each with description, code (unaltered), TryIt, and output */}

      {/* 1. length */}
      <div ref={refs.length} className="tutorial-section">
        <h2 className="section-title">1️⃣ String length</h2>
        <p>
          <b>What it does:</b> Returns the number of UTF-16 code units in the
          string. Note that some Unicode characters (e.g. many emoji) use two
          code units and so increase <code>.length</code> by 2.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "Hello";
console.log("s:", s);
console.log("length:", s.length); // 5

// Note: characters outside the BMP (like some emoji) count as 2 code units:
let e = "😊";
console.log("emoji length:", e.length); // usually 2`}
          />
          <TryItButton
            code={`let s = "Hello";
console.log("s:", s);
console.log("length:", s.length);

let e = "😊";
console.log("emoji length:", e.length);`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>s: Hello length: 5 emoji length: 2</pre>
        </div>
      </div>

      {/* 2. charAt */}
      <div ref={refs.charAt} className="tutorial-section">
        <h2 className="section-title">2️⃣ String.charAt()</h2>
        <p>
          <b>What it does:</b> Returns the character at the given zero-based
          index as a string. If the index is out of range it returns an empty
          string.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "JavaScript";
console.log("charAt(0):", s.charAt(0)); // "J"
console.log("charAt(4):", s.charAt(4)); // "S"
console.log("charAt(99):", s.charAt(99)); // "" (empty string)`}
          />
          <TryItButton
            code={`let s = "JavaScript";
console.log("charAt(0):", s.charAt(0));
console.log("charAt(4):", s.charAt(4));
console.log("charAt(99):", s.charAt(99));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>charAt(0): J charAt(4): S charAt(99): </pre>
        </div>
      </div>

      {/* 3. charCodeAt */}
      <div ref={refs.charCodeAt} className="tutorial-section">
        <h2 className="section-title">3️⃣ String.charCodeAt()</h2>
        <p>
          <b>What it does:</b> Returns the UTF-16 code unit value (a number
          between 0 and 65535) at the specified index.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "ABC";
console.log("charCodeAt(0):", s.charCodeAt(0)); // 65 (code for 'A')
console.log("charCodeAt(1):", s.charCodeAt(1)); // 66`}
          />
          <TryItButton
            code={`let s = "ABC";
console.log("charCodeAt(0):", s.charCodeAt(0));
console.log("charCodeAt(1):", s.charCodeAt(1));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>charCodeAt(0): 65 charCodeAt(1): 66</pre>
        </div>
      </div>

      {/* 4. codePointAt */}
      <div ref={refs.codePointAt} className="tutorial-section">
        <h2 className="section-title">4️⃣ String.codePointAt()</h2>
        <p>
          <b>What it does:</b> Returns the full Unicode code point value
          starting at the given index — useful for characters outside the Basic
          Multilingual Plane (emoji, rare scripts).
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let emoji = "😊";
console.log("codePointAt(0):", emoji.codePointAt(0)); // decimal
console.log("hex:", emoji.codePointAt(0).toString(16)); // hex`}
          />
          <TryItButton
            code={`let emoji = "😊";
console.log("codePointAt(0):", emoji.codePointAt(0));
console.log("hex:", emoji.codePointAt(0).toString(16));`}
          />
        </div>
        <div className="output-box">
          Output (example):
          <pre>codePointAt(0): 128522 hex: 1f60a</pre>
        </div>
      </div>

      {/* 5. concat */}
      <div ref={refs.concat} className="tutorial-section">
        <h2 className="section-title">5️⃣ String.concat()</h2>
        <p>
          <b>What it does:</b> Joins two or more strings. You can also use the{" "}
          <code>+</code> operator — <code>concat()</code> is explicit.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = "Hello";
let b = "World";
console.log(a.concat(", ", b, "!")); // "Hello, World!"
console.log(a + " " + b); // "Hello World"`}
          />
          <TryItButton
            code={`let a = "Hello";
let b = "World";
console.log(a.concat(", ", b, "!"));
console.log(a + " " + b);`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>Hello, World! Hello World</pre>
        </div>
      </div>

      {/* 6. at */}
      <div ref={refs.at} className="tutorial-section">
        <h2 className="section-title">6️⃣ String.at()</h2>
        <p>
          <b>What it does:</b> Returns a string of length 1 at the given index.
          It supports negative indices (e.g. <code>-1</code> returns the last
          character).
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "Hello";
console.log("at(0):", s.at(0)); // "H"
console.log("at(-1):", s.at(-1)); // "o" (last character)`}
          />
          <TryItButton
            code={`let s = "Hello";
console.log("at(0):", s.at(0));
console.log("at(-1):", s.at(-1));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>at(0): H at(-1): o</pre>
        </div>
      </div>

      {/* 7. bracket access [] */}
      <div ref={refs.bracket} className="tutorial-section">
        <h2 className="section-title">7️⃣ String [ ] (bracket access)</h2>
        <p>
          <b>What it does:</b> Strings behave like array-like objects for read
          access: you can use <code>str[i]</code> to get a character, but
          out-of-range access returns <code>undefined</code>.{" "}
          <code>charAt</code> returns an empty string instead.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "Hi";
console.log("s[0]:", s[0]); // "H"
console.log("s[2]:", s[2]); // undefined
console.log("charAt(2):", s.charAt(2)); // ""`}
          />
          <TryItButton
            code={`let s = "Hi";
console.log("s[0]:", s[0]);
console.log("s[2]:", s[2]);
console.log("charAt(2):", s.charAt(2));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>s[0]: H s[2]: undefined charAt(2): </pre>
        </div>
      </div>

      {/* 8. slice */}
      <div ref={refs.slice} className="tutorial-section">
        <h2 className="section-title">8️⃣ String.slice()</h2>
        <p>
          <b>What it does:</b> Extracts a section of a string from{" "}
          <code>start</code> up to (but not including) <code>end</code>. Accepts
          negative indices to count from the end.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "JavaScript";
console.log(s.slice(0,4)); // "Java"
console.log(s.slice(-6)); // "Script"`}
          />
          <TryItButton
            code={`let s = "JavaScript";
console.log(s.slice(0,4));
console.log(s.slice(-6));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>Java Script</pre>
        </div>
      </div>

      {/* 9. substring */}
      <div ref={refs.substring} className="tutorial-section">
        <h2 className="section-title">9️⃣ String.substring()</h2>
        <p>
          <b>What it does:</b> Extracts characters between two indices. If{" "}
          <code>start &gt; end</code>, arguments are swapped. It does not
          support negative indices the same way <code>slice()</code> does.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "JavaScript";
console.log(s.substring(4,10)); // "Script"
console.log(s.substring(10,4)); // also "Script" (arguments swapped)`}
          />
          <TryItButton
            code={`let s = "JavaScript";
console.log(s.substring(4,10));
console.log(s.substring(10,4));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>Script Script</pre>
        </div>
      </div>

      {/* 10. substr (deprecated) */}
      <div ref={refs.substr} className="tutorial-section">
        <h2 className="section-title">
          🔟 String.substr() — (legacy / deprecated)
        </h2>
        <p>
          <b>What it does:</b> Extracts <em>length</em> characters from{" "}
          <em>start</em>. This method is legacy and deprecated — prefer{" "}
          <code>slice()</code> or <code>substring()</code>.
        </p>
        <div className="code-demo-box" style={{ backgroundColor: "#fff7ed" }}>
          <CodeCopyBlock
            code={`let s = "JavaScript";
console.log(s.substr(4,3)); // "Scr" (from index 4, length 3)
// Use slice instead:
console.log(s.slice(4, 4 + 3)); // "Scr"`}
          />
          <TryItButton
            code={`let s = "JavaScript";
console.log(s.substr(4,3));
console.log(s.slice(4, 4 + 3));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>Scr Scr</pre>
        </div>
      </div>

      {/* 11. toUpperCase / toLowerCase */}
      <div ref={refs.toUpperCase} className="tutorial-section">
        <h2 className="section-title">1️⃣1️⃣ toUpperCase() / toLowerCase()</h2>
        <p>
          <b>What it does:</b> Convert the whole string to upper- or lower-case
          respectively. Returns a new string (strings are immutable).
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "Hello World";
console.log(s.toUpperCase()); // "HELLO WORLD"
console.log(s.toLowerCase()); // "hello world"`}
          />
          <TryItButton
            code={`let s = "Hello World";
console.log(s.toUpperCase());
console.log(s.toLowerCase());`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>HELLO WORLD hello world</pre>
        </div>
      </div>

      {/* 12. isWellFormed */}
      <div ref={refs.isWellFormed} className="tutorial-section">
        <h2 className="section-title">1️⃣2️⃣ String.isWellFormed()</h2>
        <p>
          <b>What it does:</b> Returns <code>true</code> if the string contains
          no lone UTF-16 surrogates (i.e., the string is well-formed). Useful
          when validating binary UTF-16 sequences or sanitizing input.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`// make a string with a lone high surrogate:
let loneHigh = "A" + String.fromCharCode(0xD800) + "B";
console.log("isWellFormed?:", loneHigh.isWellFormed()); // false (if environment supports it)`}
          />
          <TryItButton
            code={`let loneHigh = "A" + String.fromCharCode(0xD800) + "B";
console.log("isWellFormed?:", loneHigh.isWellFormed());`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>isWellFormed?: false</pre>
        </div>
      </div>

      {/* 13. toWellFormed */}
      <div ref={refs.toWellFormed} className="tutorial-section">
        <h2 className="section-title">1️⃣3️⃣ String.toWellFormed()</h2>
        <p>
          <b>What it does:</b> Returns a new string where lone surrogates are
          replaced by the Unicode replacement character (U+FFFD), making the
          string safe/well-formed.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let bad = "A" + String.fromCharCode(0xD800) + "B";
console.log("before:", bad);
console.log("after:", bad.toWellFormed()); // "A\uFFFDB" (if supported)`}
          />
          <TryItButton
            code={`let bad = "A" + String.fromCharCode(0xD800) + "B";
console.log("before:", bad);
console.log("after:", bad.toWellFormed());`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>before: A�B after: A�B</pre>
        </div>
      </div>

      {/* 14. trim / trimStart / trimEnd */}
      <div ref={refs.trim} className="tutorial-section">
        <h2 className="section-title">1️⃣4️⃣ trim(), trimStart(), trimEnd()</h2>
        <p>
          <b>What it does:</b> Remove whitespace from the beginning and/or end
          of a string. Useful for sanitizing user input.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let s = "   hello   ";
console.log("trim():", "'" + s.trim() + "'");
console.log("trimStart():", "'" + s.trimStart() + "'");
console.log("trimEnd():", "'" + s.trimEnd() + "'");`}
          />
          <TryItButton
            code={`let s = "   hello   ";
console.log("trim():", "'" + s.trim() + "'");
console.log("trimStart():", "'" + s.trimStart() + "'");
console.log("trimEnd():", "'" + s.trimEnd() + "'");`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>trim(): 'hello' trimStart(): 'hello ' trimEnd(): ' hello'</pre>
        </div>
      </div>

      {/* 15. padStart / padEnd */}
      <div ref={refs.padStart} className="tutorial-section">
        <h2 className="section-title">1️⃣5️⃣ padStart() / padEnd()</h2>
        <p>
          <b>What it does:</b> Pad a string to a specified length from the start
          or end with a fill string. Great for formatting numbers or fixed-width
          output.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let num = "7";
console.log(num.padStart(3, "0")); // "007"
console.log(num.padEnd(3, "-")); // "7--"`}
          />
          <TryItButton
            code={`let num = "7";
console.log(num.padStart(3, "0"));
console.log(num.padEnd(3, "-"));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>007 7--</pre>
        </div>
      </div>

      {/* 16. repeat */}
      <div ref={refs.repeat} className="tutorial-section">
        <h2 className="section-title">1️⃣6️⃣ repeat()</h2>
        <p>
          <b>What it does:</b> Repeat the string <code>n</code> times and return
          a new concatenated string.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log("ha".repeat(3)); // "hahaha"
console.log("-".repeat(5)); // "-----"`}
          />
          <TryItButton
            code={`console.log("ha".repeat(3));
console.log("-".repeat(5));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>hahaha -----</pre>
        </div>
      </div>

      {/* 17. replace */}
      <div ref={refs.replace} className="tutorial-section">
        <h2 className="section-title">1️⃣7️⃣ replace()</h2>
        <p>
          <b>What it does:</b> Replace the first occurrence when{" "}
          <code>searchValue</code> is a string. Use a RegExp with the{" "}
          <code>g</code> flag for global replacements.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "one one one";
console.log(text.replace("one", "two")); // "two one one"

// Use RegExp with g flag to replace all:
console.log(text.replace(/one/g, "two")); // "two two two"`}
          />
          <TryItButton
            code={`let text = "one one one";
console.log(text.replace("one", "two"));
console.log(text.replace(/one/g, "two"));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>two one one two two two</pre>
        </div>
      </div>

      {/* 18. replaceAll */}
      <div ref={refs.replaceAll} className="tutorial-section">
        <h2 className="section-title">1️⃣8️⃣ replaceAll()</h2>
        <p>
          <b>What it does:</b> Replace all occurrences of a substring or a
          global RegExp. This is clearer and safer than using{" "}
          <code>replace()</code> with global RegExp in many cases.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "tea tea tea";
console.log(text.replaceAll("tea", "coffee")); // "coffee coffee coffee"

// With RegExp (must be global):
console.log(text.replaceAll(/tea/g, "coffee")); // same result`}
          />
          <TryItButton
            code={`let text = "tea tea tea";
console.log(text.replaceAll("tea", "coffee"));
console.log(text.replaceAll(/tea/g, "coffee"));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>coffee coffee coffee coffee coffee coffee</pre>
        </div>
      </div>

      {/* 19. split */}
      <div ref={refs.split} className="tutorial-section">
        <h2 className="section-title">1️⃣9️⃣ split()</h2>
        <p>
          <b>What it does:</b> Split a string into an array of substrings using
          a separator. Optionally pass a limit to cap the resulting array
          length.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let csv = "apple,banana,orange";
let arr = csv.split(",");
console.log(arr); // ["apple","banana","orange"]

// split with limit:
console.log(csv.split(",", 2)); // ["apple","banana"]`}
          />
          <TryItButton
            code={`let csv = "apple,banana,orange";
let arr = csv.split(",");
console.log(arr);
console.log(csv.split(",", 2));`}
          />
        </div>
        <div className="output-box">
          Output:
          <pre>["apple","banana","orange"] ["apple","banana"]</pre>
        </div>
      </div>

      {/* Next steps / footer */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice combining methods (e.g.,{" "}
            <code>trim().toLowerCase().split(" ")</code>).
          </li>
          <li>
            Learn RegExp for powerful text matching and use with{" "}
            <code>match</code>, <code>replace</code>, <code>matchAll</code>.
          </li>
          <li>
            When working with Unicode (emoji, rare scripts), prefer{" "}
            <code>codePointAt</code> / <code>fromCodePoint</code> and be mindful
            of <code>.length</code> counting UTF-16 code units.
          </li>
        </ul>
      </div>

      {/* ===== Styles used inline above referenced here for clarity; you can keep them in your CSS too ===== */}
      <style jsx>{`
        .code-demo-box {
          margin-top: 1rem;
          padding: 10px;
          font-size: 1.2rem;
          border-radius: 8px;
          background-color: #e4e4e4;
          box-shadow: 0 0 10px #6c6c6c;
        }
        .output-box {
          margin-top: 1rem;
          font-size: 1.05rem;
          padding: 10px;
          border: 2px dashed #2563eb;
          border-radius: 8px;
          white-space: pre-wrap;
        }
      `}</style>
    </div>
  );
}
